import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  App,
  AutoComplete,
  Avatar,
  Button,
  Card,
  Empty,
  Flex,
  List,
  Skeleton,
  Space,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CheckCircleFilled,
  DeleteOutlined,
} from "@ant-design/icons";
import {
  RecommendedAccount,
  UserTypes,
  useGetUsersQuery,
  useListRecommendedAccountsQuery,
  useSetRecommendedAccountsMutation,
} from "api";
import { useAppSelector } from "store/hooks";
import { displayUserName, getRouteByName } from "libs";
import { getErrorMessage } from "components";

/**
 * Admin-curated strip for the Explore tab (#12b). Simple reorderable
 * list with up/down buttons — drag-n-drop would be nicer but adds a
 * dep (react-dnd / @hello-pangea/dnd); arrows are zero-cost and good
 * enough for a 10–15 row list.
 *
 * Adding new accounts: separate "Add user" search modal is deferred —
 * for MVP we expose a numeric user id input. The full search-by-name
 * picker lives in a follow-up (uses the existing /users/search endpoint).
 */
export const RecommendedAccountsPage = () => {
  const navigate = useNavigate();
  const { notification } = App.useApp();

  const { isAll, isRecommendedAccountsRead, isRecommendedAccountsEdit } =
    useAppSelector((state) => state.loggedAdmin.parsedPermissions);

  const canRead =
    isAll || isRecommendedAccountsRead || isRecommendedAccountsEdit;
  const canEdit = isAll || isRecommendedAccountsEdit;

  const { data, isLoading } = useListRecommendedAccountsQuery(undefined, {
    skip: !canRead,
  });

  const [setAll, { isLoading: isSaving }] = useSetRecommendedAccountsMutation();

  // Local copy of the ordered list — mutations stay client-side until
  // the user clicks "Save", which posts the new ordering.
  const [draft, setDraft] = useState<RecommendedAccount[]>([]);

  useEffect(() => {
    if (!canRead) {
      navigate(getRouteByName("NotFoundPage"));
    }
  }, [canRead, navigate]);

  useEffect(() => {
    if (data) {
      setDraft(data);
    }
  }, [data]);

  // Tracks whether the draft diverges from the server copy so we can
  // disable Save when nothing changed.
  const isDirty = useMemo(() => {
    if (!data) return false;
    if (data.length !== draft.length) return true;
    return data.some((row, i) => row.user?.id !== draft[i]?.user?.id);
  }, [data, draft]);

  const move = (index: number, delta: number) => {
    const targetIndex = index + delta;
    if (targetIndex < 0 || targetIndex >= draft.length) return;
    const next = [...draft];
    [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
    setDraft(next);
  };

  const remove = (index: number) => {
    const next = draft.filter((_, i) => i !== index);
    setDraft(next);
  };

  // Live user search via the existing admin endpoint. Skip until the
  // admin types 2+ chars so the dropdown stays empty on mount.
  const [searchTerm, setSearchTerm] = useState<string>("");
  const { data: userMatches, isFetching: isSearching } = useGetUsersQuery(
    { search: searchTerm, page: 1, perPage: 12 },
    { skip: searchTerm.trim().length < 2 },
  );

  /**
   * Format a search hit for AntD AutoComplete. The `value` is the
   * stringified user id (AutoComplete contract — strings only); the
   * `label` is a rich JSX node with avatar + names + a clear
   * "verified" indicator so the admin can spot blue-checks instantly.
   */
  const searchOptions = useMemo(() => {
    const rows =
      (userMatches as { data?: Array<Record<string, unknown>> })?.data ?? [];
    return rows
      .filter((u) => {
        // Hide users already in the draft to avoid double-add.
        const id = Number(u.id);
        return !draft.some((row) => row.user?.id === id);
      })
      .map((u) => {
        const id = Number(u.id);
        const username = String(u.username ?? "");
        const first = String(u.firstName ?? u.first_name ?? "");
        const last = String(u.lastName ?? u.last_name ?? "");
        const verifiedUntil = u.verifiedUntil ?? u.verified_until;
        const isVerified =
          verifiedUntil !== undefined &&
          verifiedUntil !== null &&
          new Date(verifiedUntil as string).getTime() > Date.now();
        const avatar = (u.profilePicture as string | null | undefined) ?? null;
        // Company (JURIDICAL) accounts surface under their company name —
        // thread `type`/`company` through so displayUserName can resolve it.
        const type = typeof u.type === "number" ? (u.type as UserTypes) : undefined;
        const company =
          (u.company as
            | { id: number; name: string; logo?: string | null }
            | null
            | undefined) ?? null;
        const displayName = displayUserName({
          firstName: first,
          lastName: last,
          type,
          company,
        });
        return {
          value: String(id),
          // raw fields stashed so onSelect can splice them into draft
          // without a second fetch.
          _id: id,
          _username: username,
          _first: first,
          _last: last,
          _avatar: avatar,
          _verified: isVerified,
          _type: type,
          _company: company,
          label: (
            <Flex align="center" gap={10}>
              <Avatar src={avatar ?? undefined} size={28}>
                {(first || username || "?").charAt(0).toUpperCase()}
              </Avatar>
              <Flex vertical style={{ minWidth: 0, flex: 1 }}>
                <Typography.Text strong style={{ fontSize: 13 }}>
                  {displayName !== "-" ? displayName : `@${username}`}
                </Typography.Text>
                <Typography.Text
                  type="secondary"
                  style={{ fontSize: 11 }}
                >
                  @{username}
                </Typography.Text>
              </Flex>
              {isVerified && (
                <Tag
                  color="blue"
                  icon={<CheckCircleFilled />}
                  style={{ marginInlineEnd: 0 }}
                >
                  Verificat
                </Tag>
              )}
            </Flex>
          ),
        };
      });
  }, [userMatches, draft]);

  const handleAdd = (
    _value: string,
    option: {
      _id: number;
      _username: string;
      _first: string;
      _last: string;
      _avatar: string | null;
      _type?: UserTypes;
      _company?: { id: number; name: string; logo?: string | null } | null;
    },
  ) => {
    if (draft.some((row) => row.user?.id === option._id)) {
      notification.warning({ message: "Utilizatorul e deja în listă." });
      return;
    }
    setDraft([
      ...draft,
      {
        id: -Date.now(),
        position: draft.length,
        user: {
          id: option._id,
          username: option._username,
          firstName: option._first || null,
          lastName: option._last || null,
          profilePicture: option._avatar,
          // Carry company info so JURIDICAL accounts render their company
          // name in the list row without a second fetch.
          type: option._type,
          company: option._company,
        },
      },
    ]);
    // Clear the autocomplete so the admin can immediately add the next.
    setSearchTerm("");
  };

  const handleSave = async () => {
    try {
      const userIds = draft
        .map((row) => row.user?.id)
        .filter((id): id is number => typeof id === "number");
      await setAll({ userIds }).unwrap();
      notification.success({ message: "Lista a fost salvată." });
    } catch (e) {
      notification.error({ message: getErrorMessage(e as never) });
    }
  };

  if (isLoading && !data) {
    return <Skeleton active paragraph={{ rows: 6 }} />;
  }

  return (
    <Flex vertical gap={16}>
      <Flex
        justify="space-between"
        align="center"
        gap={16}
        wrap="wrap"
      >
        <Typography.Title level={3} style={{ margin: 0 }}>
          Conturi recomandate
        </Typography.Title>
        {canEdit && (
          <Button
            type="primary"
            loading={isSaving}
            disabled={!isDirty}
            onClick={handleSave}
          >
            Salvează ordinea
          </Button>
        )}
      </Flex>

      <Typography.Paragraph type="secondary">
        Aceste conturi apar în partea de sus a tab-ului Explore din aplicația
        mobilă, întotdeauna vizibile. Recomandat: 10-15 conturi. Caută după
        nume sau username pentru a adăuga; folosește săgețile pentru a
        reordona. Conturile cu bifa albastră au abonamentul "Verificat"
        activ.
      </Typography.Paragraph>

      {canEdit && (
        <Card size="small">
          <AutoComplete
            value={searchTerm}
            onSearch={(v) => setSearchTerm(v)}
            onChange={(v) => setSearchTerm(v ?? "")}
            onSelect={(value, option) =>
              handleAdd(value, option as never)
            }
            options={searchOptions}
            notFoundContent={
              searchTerm.trim().length < 2
                ? "Tastează cel puțin 2 caractere"
                : isSearching
                  ? "Caut..."
                  : <Empty description="Niciun rezultat" />
            }
            placeholder="Caută utilizator de adăugat (nume sau @username)..."
            style={{ width: "100%" }}
            allowClear
          />
        </Card>
      )}

      <Card>
        <List
          dataSource={draft}
          locale={{ emptyText: "Niciun cont în listă încă." }}
          renderItem={(row, index) => (
            <List.Item
              actions={
                canEdit
                  ? [
                      <Tooltip title="Sus" key="up">
                        <Button
                          size="small"
                          icon={<ArrowUpOutlined />}
                          disabled={index === 0}
                          onClick={() => move(index, -1)}
                        />
                      </Tooltip>,
                      <Tooltip title="Jos" key="down">
                        <Button
                          size="small"
                          icon={<ArrowDownOutlined />}
                          disabled={index === draft.length - 1}
                          onClick={() => move(index, 1)}
                        />
                      </Tooltip>,
                      <Tooltip title="Șterge" key="del">
                        <Button
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => remove(index)}
                        />
                      </Tooltip>,
                    ]
                  : []
              }
            >
              <List.Item.Meta
                avatar={
                  <Avatar
                    src={row.user?.profilePicture ?? undefined}
                    size={48}
                  >
                    {row.user?.username?.[0]?.toUpperCase() ?? "?"}
                  </Avatar>
                }
                title={
                  <Flex align="center" gap={6}>
                    <span>
                      {row.user
                        ? displayUserName(row.user) !== "-"
                          ? displayUserName(row.user)
                          : `@${row.user.username}`
                        : "Utilizator necunoscut"}
                    </span>
                    {/* Verified flag lifted out of the search dropdown
                        so the admin can see at a glance which already-
                        added accounts are verified (instead of having
                        to leave the page to check). */}
                    {row.user?.hasVerifiedBadge && (
                      <Tag
                        color="blue"
                        icon={<CheckCircleFilled />}
                        style={{ marginInlineEnd: 0 }}
                      >
                        Verificat
                      </Tag>
                    )}
                  </Flex>
                }
                description={
                  row.user?.username ? `@${row.user.username}` : "—"
                }
              />
            </List.Item>
          )}
        />
      </Card>
    </Flex>
  );
};
